import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { Evidence, FilterState, SortOptions, Entity } from '../types/types';
import evidenceWrapper from '../data/evidence_parallel.json';
import { useLocation, useNavigate } from 'react-router-dom';
import { filtersToUrlParams, urlParamsToFilters } from '../utils/urlUtils';
import { adaptParallelEvidence } from '../utils/dataAdapter';
import { useEvidenceEnhancement } from '../hooks/useEvidenceWithSupabase';
import { sortPeriodsChronologically } from '../utils/displayUtils';

interface DataContextType {
  allEvidence: Evidence[];
  filteredEvidence: Evidence[];
  selectedEvidence: Evidence | null;
  setSelectedEvidence: (evidence: Evidence | null) => void;
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  updateFilter: <K extends keyof FilterState>(
    filterType: K,
    value: FilterState[K]
  ) => void;
  sortOptions: SortOptions;
  setSortOptions: (options: SortOptions) => void;
  uniqueShiftTypes: string[];
  uniqueSystems: string[];
  uniqueReportingPeriods: string[];
  uniqueStatuses: string[];
  uniqueImplementationStages: string[];
  isLoading: boolean;
  error: string | null;
  allEntities: Entity[];
  filterEvidence: (entities: string[], searchTerm: string) => void;
  resetFilters: () => void;
  evidenceCount: number;
  setEvidenceCount: (value: number | ((prevCount: number) => number)) => void;
  isEvidenceModalOpen: boolean;
  openEvidenceModal: () => void;
  closeEvidenceModal: () => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize all arrays with empty arrays to prevent map errors
  const [baseEvidence, setBaseEvidence] = useState<Evidence[]>([]);
  const [allEvidence, setAllEvidence] = useState<Evidence[]>([]);
  const [filteredEvidence, setFilteredEvidence] = useState<Evidence[]>([]);
  const [selectedEvidence, setSelectedEvidence] = useState<Evidence | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    shiftTypes: [],
    systems: [],
    confidenceRange: [0, 5],
    selectedEntities: [],
    reportingPeriods: [],
    statuses: [],
    implementationStages: [],
    selectedModules: [],
    selectedStreams: [],
  });
  const [sortOptions, setSortOptions] = useState<SortOptions>({
    field: 'id',
    direction: 'asc',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allEntities, setAllEntities] = useState<Entity[]>([]);
  const [evidenceCount, setEvidenceCount] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal state
  const [isEvidenceModalOpen, setIsEvidenceModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Sync URL parameters with filter state and search term - only on evidence pages
  useEffect(() => {
    if (location.pathname.startsWith('/evidence') && !location.pathname.includes('/item')) {
      const params = new URLSearchParams(location.search);
      const { filters: urlFilters, searchTerm: urlSearchTerm } = urlParamsToFilters(params);
      
      // Only update if URL params exist and are different from current filters
      if (Object.keys(urlFilters).length > 0) {
        setFilters(prevFilters => ({
          ...prevFilters,
          ...urlFilters
        }));
      }
      
      // Update search term if it exists in URL
      if (urlSearchTerm !== undefined) {
        setSearchTerm(urlSearchTerm);
      }
    }
  }, [location.search, location.pathname]);
  
  // Update URL when filters or search term change - only on evidence pages
  useEffect(() => {
    if (location.pathname === '/evidence') {
      const params = filtersToUrlParams(filters, searchTerm);
      const currentSearch = new URLSearchParams(location.search);
      
      // Compare current URL params with new filter params to avoid unnecessary navigation
      let isDifferent = false;
      
      // Check if params are different
      if (params.toString() !== currentSearch.toString()) {
        isDifferent = true;
      }
      
      // Only update URL if params are different
      if (isDifferent) {
        navigate({
          pathname: location.pathname,
          search: params.toString()
        }, { replace: true });
      }
    }
  }, [filters, searchTerm, location.pathname, navigate]);
  
  // Open modal when evidence is selected
  useEffect(() => {
    if (selectedEvidence) {
      setIsEvidenceModalOpen(true);
    }
  }, [selectedEvidence]);
  
  // Modal functions
  const openEvidenceModal = () => setIsEvidenceModalOpen(true);
  const closeEvidenceModal = () => {
    setIsEvidenceModalOpen(false);
    // Note: We don't clear selectedEvidence here to allow it to persist after modal is closed
  };

  // Load data from JSON file
  useEffect(() => {
    try {
      // Simulate API delay
      setTimeout(() => {
        try {
          const adaptedData = adaptParallelEvidence(evidenceWrapper as any);
          setBaseEvidence(adaptedData); // Store base evidence without tags
          setAllEvidence(adaptedData);
          setFilteredEvidence(adaptedData);
          setIsLoading(false);
          
          // Extract unique entities and count their occurrences
          const entitiesMap = new Map<string, number>();
          
          adaptedData.forEach(item => {
            if (item.entities && Array.isArray(item.entities)) {
              item.entities.forEach(entity => {
                entitiesMap.set(entity, (entitiesMap.get(entity) || 0) + 1);
              });
            }
          });
          
          const entityList: Entity[] = Array.from(entitiesMap.entries()).map(([name, count]) => ({
            name,
            count
          })).sort((a, b) => b.count - a.count);
          
          setAllEntities(entityList);
        } catch (e) {
          console.error('Error parsing data:', e);
          setError('Error parsing data');
          setIsLoading(false);
          // Ensure arrays are initialized even in error case
          setAllEvidence([]);
          setFilteredEvidence([]);
          setAllEntities([]);
        }
      }, 100);
    } catch (e) {
      console.error('Error loading data:', e);
      setError('Error loading data');
      setIsLoading(false);
      // Ensure arrays are initialized even in error case
      setAllEvidence([]);
      setFilteredEvidence([]);
      setAllEntities([]);
    }
  }, []);

  // Enhance evidence with tags from Supabase
  const { enhancedEvidence } = useEvidenceEnhancement(baseEvidence);
  
  // Update all evidence when enhancement is complete
  useEffect(() => {
    if (enhancedEvidence && enhancedEvidence.length > 0) {
      console.log('DataContext - Setting enhanced evidence with', enhancedEvidence.length, 'items');
      setAllEvidence(enhancedEvidence);
    }
  }, [enhancedEvidence]);

  // Get unique values for filter options
  const uniqueShiftTypes = useMemo(() => {
    if (!allEvidence || !Array.isArray(allEvidence)) return [];
    
    const types = new Set<string>();
    allEvidence.forEach((evidence) => {
      if (evidence && evidence.shift_type) {
        types.add(evidence.shift_type);
      }
    });
    return Array.from(types);
  }, [allEvidence]);

  const uniqueSystems = useMemo(() => {
    if (!allEvidence || !Array.isArray(allEvidence)) return [];
    
    const systems = new Set<string>();
    allEvidence.forEach((evidence) => {
      if (evidence && evidence.context?.systems_affected) {
        const raw = evidence.context.systems_affected;
        raw.split(/[;,]+/).forEach((sys) => {
          const trimmed = sys.trim();
          if (trimmed) systems.add(trimmed);
        });
      }
    });
    return Array.from(systems);
  }, [allEvidence]);

  const uniqueReportingPeriods = useMemo(() => {
    if (!allEvidence || !Array.isArray(allEvidence)) return [];
    
    const periods = new Set<string>();
    allEvidence.forEach((evidence) => {
      if (evidence && evidence.reporting_period?.label) {
        periods.add(evidence.reporting_period.label);
      }
    });
    return sortPeriodsChronologically(Array.from(periods));
  }, [allEvidence]);

  const uniqueStatuses = useMemo(() => {
    if (!allEvidence || !Array.isArray(allEvidence)) return [];
    
    const statuses = new Set<string>();
    allEvidence.forEach((evidence) => {
      if (evidence && evidence.temporal_status) {
        statuses.add(evidence.temporal_status);
      }
    });
    return Array.from(statuses).sort();
  }, [allEvidence]);

  const uniqueImplementationStages = useMemo(() => {
    if (!allEvidence || !Array.isArray(allEvidence)) return [];
    
    const stages = new Set<string>();
    allEvidence.forEach((evidence) => {
      if (evidence && evidence.implementation_stage?.status) {
        stages.add(evidence.implementation_stage.status);
      }
    });
    return Array.from(stages).sort();
  }, [allEvidence]);

  // Update a specific filter
  const updateFilter = <K extends keyof FilterState>(
    filterType: K,
    value: FilterState[K]
  ) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  // Apply filters and sort to evidence
  useMemo(() => {
    if (!allEvidence || !Array.isArray(allEvidence)) {
      setFilteredEvidence([]);
      return;
    }
    
    let filtered = [...allEvidence];

    // Apply filters
    if (filters.shiftTypes.length > 0) {
      filtered = filtered.filter((evidence) =>
        filters.shiftTypes.includes(evidence.shift_type)
      );
    }

    if (filters.systems.length > 0) {
      filtered = filtered.filter((evidence) => {
        if (!evidence.context?.systems_affected) return false;
        const evidenceSystems = evidence.context.systems_affected
          .split(/[;,]+/)
          .map(s => s.trim())
          .filter(s => s);
        return evidenceSystems.some(sys => filters.systems.includes(sys));
      });
    }

    if (filters.confidenceRange[0] > 0 || filters.confidenceRange[1] < 100) {
      filtered = filtered.filter((evidence) => {
        if (evidence.confidence === null) return false;
        const confidence = evidence.confidence;
        return (
          confidence >= filters.confidenceRange[0] &&
          confidence <= filters.confidenceRange[1]
        );
      });
    }

    if (filters.selectedEntities.length > 0) {
      filtered = filtered.filter((evidence) =>
        evidence.entities && Array.isArray(evidence.entities) && 
        evidence.entities.some((entity) =>
          filters.selectedEntities.includes(entity)
        )
      );
    }

    // Filter by reporting periods if any selected
    if (filters.reportingPeriods && filters.reportingPeriods.length > 0) {
      filtered = filtered.filter((evidence) =>
        evidence.reporting_period?.label &&
        filters.reportingPeriods!.includes(evidence.reporting_period.label)
      );
    }

    // Filter by statuses if any selected
    if (filters.statuses && filters.statuses.length > 0) {
      filtered = filtered.filter((evidence) =>
        evidence.temporal_status &&
        filters.statuses!.includes(evidence.temporal_status)
      );
    }

    // Filter by implementation stages if any selected
    if (filters.implementationStages && filters.implementationStages.length > 0) {
      filtered = filtered.filter((evidence) =>
        evidence.implementation_stage?.status &&
        filters.implementationStages!.includes(evidence.implementation_stage.status)
      );
    }

    // Filter by impact modules if any selected
    if (filters.selectedModules && filters.selectedModules.length > 0) {
      filtered = filtered.filter((evidence) => {
        if (!evidence.tags || evidence.tags.length === 0) return false;
        return evidence.tags.some(tag => 
          filters.selectedModules!.includes(tag.module_id)
        );
      });
    }

    // Filter by impact streams if any selected
    if (filters.selectedStreams && filters.selectedStreams.length > 0) {
      filtered = filtered.filter((evidence) => {
        if (!evidence.tags || evidence.tags.length === 0) return false;
        return evidence.tags.some(tag => 
          filters.selectedStreams!.includes(tag.stream_id)
        );
      });
    }

    // Apply sort
    filtered.sort((a, b) => {
      const field = sortOptions.field;
      
      // Handle null values
      if (a[field] === null && b[field] === null) return 0;
      if (a[field] === null) return sortOptions.direction === 'asc' ? -1 : 1;
      if (b[field] === null) return sortOptions.direction === 'asc' ? 1 : -1;
      
      // Sort based on field type
      if (typeof a[field] === 'string' && typeof b[field] === 'string') {
        return sortOptions.direction === 'asc'
          ? (a[field] as string).localeCompare(b[field] as string)
          : (b[field] as string).localeCompare(a[field] as string);
      }
      
      // For numeric or date fields
      return sortOptions.direction === 'asc'
        ? (a[field] as number) - (b[field] as number)
        : (b[field] as number) - (a[field] as number);
    });

    setFilteredEvidence(filtered);
  }, [allEvidence, filters, sortOptions]);

  const filterEvidence = (entities: string[], searchTerm: string) => {
    if (!allEvidence || !Array.isArray(allEvidence)) {
      setFilteredEvidence([]);
      return;
    }
    
    let filtered = [...allEvidence];
    
    if (entities.length > 0) {
      filtered = filtered.filter(evidence => 
        evidence.entities && Array.isArray(evidence.entities) &&
        entities.some(entity => evidence.entities.includes(entity))
      );
    }
    
    if (searchTerm) {
      filtered = filtered.filter(evidence => 
        evidence.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredEvidence(filtered);
  };
  
  const resetFilters = () => {
    setFilteredEvidence(allEvidence || []);
  };

  const value = {
    allEvidence,
    filteredEvidence,
    selectedEvidence,
    setSelectedEvidence,
    filters,
    setFilters,
    updateFilter,
    sortOptions,
    setSortOptions,
    uniqueShiftTypes,
    uniqueSystems,
    uniqueReportingPeriods,
    uniqueStatuses,
    uniqueImplementationStages,
    isLoading,
    error,
    allEntities,
    filterEvidence,
    resetFilters,
    evidenceCount,
    setEvidenceCount,
    isEvidenceModalOpen,
    openEvidenceModal,
    closeEvidenceModal,
    searchTerm,
    setSearchTerm
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useData = (): DataContextType => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}; 